/**
 * This is a Pill Selector Component.This Component is the Collection of thwe Pills which is to be displayed.The Component take the array of list data of message ,with active and disabled state.This compoenent can eb Used to Display various sizes in the product discription page.
 *
 * @module views/Atoms/PillSelector
 */
import './PillSelector.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

import SelectablePill from '@ulta/modules/SelectablePill/SelectablePill';
import Tag from '@ulta/modules/Tag/Tag';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import ProductDimension from '../ProductDimension/ProductDimension';

/**
 * Represents a PillSelector component
 *
 * @method
 * @param { PillSelectorProps } props - React properties passed from composition
 * @returns PillSelector
 */

export const PillSelector = function( props ){
  const {
    dataCapture,
    variants,
    typeLabel
  } = props;

  const pillSelectorRef = useRef( null );

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  const { inflection } = useDeviceInflection();
  const selectedValue = variants.find( variant => variant.selected === true )?.name;

  const renderPills = ( item, index ) => {
    /* Accessibility (a11y): Generate a globally unique, collision-proof ID using the rail's instance UUID and item index. */
    const tagsId = item.tags?.length > 0 ? `${railUuid}-pill-tags-${index}` : undefined;
    const isUnavailableWithLabel = item.unavailable && item.outofStockLabel;
    const screenReaderText = isUnavailableWithLabel ? `${item.name} ${item.outofStockLabel}` : undefined;

    return (
      <>
        <div className='PillSelector__pill'>
          <SelectablePill
            key={ item.name }
            active={ item.selected }
            disabled={ item.disabled }
            unavailable={ item.unavailable }
            message={ item.name }
            screenReaderText={ screenReaderText }
            ariaDescribedBy={ tagsId } /* Link the button/link to the tag description */
            selectAction={ item.selectAction }
            linkSelectAction={ item.linkSelectAction }
            onClick={ () => {
              props.selectAction( item );
            } }
          />
        </div>
        { ( item.tags?.length > 0 &&
          <div id={ tagsId } className='PillSelector__tags'>
            { item.tags.map( ( tag, tagIndex ) => (
              <Tag
                key={ `${tagIndex}-${tag.message}` }
                boldText={ tag.message }
                size={ tag.size }
                variant={ tag.variant }
                backgroundColor={ tag.backgroundColor }
                textColor={ tag.textColor }
              />
            ) ) }
          </div>
        ) }
      </>
    );
  };

  return (
    <div className='PillSelector'>
      <ProductDimension
        type={ typeLabel }
        description={ selectedValue }
      />
      <div className='PillSelector__pills'>
        { inflection.MOBILE &&
        <Rail
          railRef={ pillSelectorRef }
          dataCapture={ dataCapture }
          id={ railUuid }
          useFreeMode={ true }
          spaceBetween={ 8 }
          sliderPerViewMD={ 'auto' }
          sliderPerViewSM={ 'auto' }
        >
          { variants.length > 1 && variants.map( ( item, index ) => {
            return (
              <Slide key={ index } className={ classNames( { 'hasTags': item.tags?.length > 0 } ) }>
                { renderPills( item, index ) }
              </Slide>
            );
          } )
          }
        </Rail>
        }
        { inflection.DESKTOP &&
        <ul className='PillSelector__pills--inline'>
          { variants.length > 1 && variants.map( ( item, index ) => {
            return (
              <li key={ index }
                className={ classNames( { 'PillSelector__pills--inline__item': true, 'hasTags': item.tags?.length > 0 } ) }
              >
                { renderPills( item, index ) }
              </li>
            );
          } )
          }
        </ul>
        }
      </div>
    </div>
  );
};

/**
 * property type definitions
 * @typedef PillSelectorProps
 * @type object
 * @property {Array.<{ name: String, shadeDescription: String, swatchImage: object, smooshImage: object, selected: bool, disabled: bool, unavailable: bool, outofStockLabel: String, listPrice: String, salePrice: String, selectAction: object  }>} variants - this is array of Pills
 * @property {string} typeLabel - This is the value for Label.
 */
export const propTypes =  {
  productId: PropTypes.string,
  skuId: PropTypes.string,
  /** This is array of Pills */
  variants: PropTypes.arrayOf(
    PropTypes.shape( {
      name: PropTypes.string,
      shadeDescription: PropTypes.string,
      swatchImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      smooshImage: PropTypes.shape( {
        imageUrl: PropTypes.string,
        description: PropTypes.string
      } ),
      selected: PropTypes.bool,
      disabled: PropTypes.bool,
      unavailable: PropTypes.bool,
      outofStockLabel: PropTypes.string,
      listPrice: PropTypes.string,
      salePrice: PropTypes.string
    } )
  ).isRequired,
  selectAction: PropTypes.func,
  typeLabel: PropTypes.string
};

PillSelector.propTypes = propTypes;

export default PillSelector;